/**
 * Help §5.12 — Tips & FAQ.
 *
 * The last topic, so the spec's cross-reference to Clients ("see the Clients
 * section below") points UP the page here: Clients is topic 12, this is 13.
 * Rendered as a real in-page jump rather than prose direction.
 */
import { helpSectionMeta } from '../help-sections.js';
import {
  Btn,
  Bullets,
  HelpSectionCard,
  Li,
  Screen,
  SectionLink,
  SubHead,
} from '../HelpPrimitives.js';

export function Tips(): React.JSX.Element {
  const meta = helpSectionMeta('tips');
  return (
    <HelpSectionCard meta={meta}>
      <SubHead>Handy tips</SubHead>
      <Bullets>
        <Li>
          <strong className="font-semibold text-text-strong">Save as you go.</strong> Use{' '}
          <Btn>Save Information</Btn> in <Screen>Pre-Kickoff</Screen> so you never lose work.
        </Li>
        <Li>
          <strong className="font-semibold text-text-strong">
            Always preview before approving.
          </strong>{' '}
          The AI is good, but you’re the expert — read first.
        </Li>
        <Li>
          <strong className="font-semibold text-text-strong">Regenerate beats retyping.</strong> For
          big changes, ask the AI to redo a document with <Btn>Regeneration instructions</Btn>{' '}
          rather than editing it line by line.
        </Li>
        <Li>
          <strong className="font-semibold text-text-strong">Use the filters.</strong> With many
          projects, <Btn>Filter by client…</Btn> / <Btn>Filter by position…</Btn> on the main screen
          is the fastest way to find one.
        </Li>
        <Li>
          <strong className="font-semibold text-text-strong">Log out on shared computers</strong>{' '}
          using <Btn>Log out</Btn> at the bottom of the sidebar.
        </Li>
      </Bullets>

      <SubHead>Frequently asked questions</SubHead>
      <dl className="space-y-4">
        <Faq
          q={
            <>
              <Btn>Confirm &amp; Move to Final</Btn> is greyed out
            </>
          }
        >
          Some document cards aren’t approved yet — approve every card first.
        </Faq>

        <Faq q="My client emails aren’t showing up">
          Connect Outlook via the sidebar, then click <Btn>Check Mail</Btn> inside the Inbox.
        </Faq>

        <Faq q="Can I change a document after the AI writes it?">
          Yes — edit it directly, or use <Btn>Regenerate</Btn> with a{' '}
          <Btn>Regeneration instructions</Btn> note.
        </Faq>

        <Faq q="Where’s my client / contact list?">
          See the <SectionLink to="clients">Clients</SectionLink> section.
        </Faq>
      </dl>
    </HelpSectionCard>
  );
}

/** One question/answer pair. */
function Faq({
  q,
  children,
}: {
  readonly q: React.ReactNode;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="rounded-md border border-border bg-surface-muted p-4">
      <dt className="flex items-start gap-2.5 text-[15px] font-semibold text-text-strong">
        <i className="fa-solid fa-circle-question mt-0.5 shrink-0 text-accent" aria-hidden />
        <span>{q}</span>
      </dt>
      <dd className="mt-2 pl-[1.6rem] text-[15px] leading-relaxed text-text">{children}</dd>
    </div>
  );
}
